import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { initialForm, STEP_META } from "./onboarding/constants";
import { StepWelcome } from "./onboarding/StepWelcome";
import { StepCompany } from "./onboarding/StepCompany";
import { StepOffer } from "./onboarding/StepOffer";
import { StepICP } from "./onboarding/StepICP";
import { StepStrategy } from "./onboarding/StepStrategy";
import { StepReview } from "./onboarding/StepReview";
import { FeedbackBox } from "./onboarding/FeedbackBox";

/**
 * Onboarding — 6-step analysis flow (0: welcome, 1–4: MCQ, 5: review).
 * After steps 2–4, a FeedbackBox collects satisfaction for continuous improvement.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  /** phase: "form" = filling the step, "feedback" = satisfaction loop before advancing */
  const [phase, setPhase] = useState("form");
  const [editingFrom, setEditingFrom] = useState(null);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const progress = step >= 1 && step <= 4 ? ((step - 1) / 3) * 100 : step >= 5 ? 100 : 0;

  const next = () => {
    // Steps 2, 3, 4 trigger feedback loop before advancing
    if ([2, 3, 4].includes(step) && phase === "form") {
      setPhase("feedback");
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      return;
    }
    advance();
  };

  const advance = () => {
    setPhase("form");
    if (editingFrom === 5) {
      setEditingFrom(null);
      setStep(5);
      return;
    }
    setStep((s) => Math.min(s + 1, 5));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitFeedback = async (payload) => {
    try {
      await api.post("/onboarding/feedback", {
        step,
        satisfaction: payload.satisfaction,
        comment: payload.comment || "",
      });
    } catch {
      // Feedback is best-effort; don't block the user.
    }
    advance();
  };

  const skipFeedback = () => advance();

  const prev = () => {
    if (phase === "feedback") {
      setPhase("form");
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goEdit = (target) => {
    setEditingFrom(5);
    setPhase("form");
    setStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      if (form.final_feedback?.trim()) {
        await api.post("/onboarding/feedback", {
          step: 5,
          satisfaction: "final",
          comment: form.final_feedback.trim(),
        }).catch(() => {});
      }
      await refresh();
      toast.success("Votre analyse est prête !");
      navigate("/dashboard");
    } catch {
      toast.error("Erreur d'enregistrement, réessayez");
    } finally {
      setSaving(false);
    }
  };

  /* ── Step 0 ── */
  if (step === 0) return <StepWelcome onContinueSolo={() => setStep(1)} />;

  /* ── Step 5 ── */
  if (step === 5)
    return (
      <StepReview
        form={form}
        onBack={() => setStep(4)}
        onEdit={goEdit}
        onSubmit={submit}
        onRestart={() => { setStep(1); setPhase("form"); }}
        saving={saving}
        onFinalFeedbackChange={(v) => up("final_feedback", v)}
      />
    );

  /* ── Steps 1–4 ── */
  return (
    <div
      className="min-h-screen bg-[var(--bg)] text-[var(--text)]"
      data-testid={`onboarding-step-${step}`}
    >
      {/* Progress header */}
      <div className="sticky top-0 z-20 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center gap-3">
          <button
            onClick={prev}
            className="w-10 h-10 rounded-lg border border-[var(--border)] grid place-items-center active:scale-95 shrink-0 text-zinc-300 hover:border-zinc-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <p className="hp-label" style={{ color: "#86efac" }}>
              Étape {step} sur 4{editingFrom === 5 ? " · Révision" : ""}
              {phase === "feedback" && " · Feedback"}
            </p>
            <div className="h-1.5 bg-[var(--surface-alt)] rounded-full mt-1.5 overflow-hidden border border-[var(--border-soft)]">
              <div
                data-testid="onboarding-progress"
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          {editingFrom === 5 && (
            <button
              onClick={() => { setEditingFrom(null); setStep(5); setPhase("form"); }}
              className="text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 shrink-0 transition-colors"
            >
              ← Analyse
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-5 pt-6 pb-40">
        <div className="mb-5">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-[var(--text)] tracking-tight leading-tight">
            {STEP_META[step].title}
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 mt-1.5">{STEP_META[step].sub}</p>
        </div>

        {phase === "form" && step === 1 && <StepCompany form={form} up={up} />}
        {phase === "form" && step === 2 && <StepOffer form={form} up={up} />}
        {phase === "form" && step === 3 && <StepICP  form={form} up={up} />}
        {phase === "form" && step === 4 && <StepStrategy form={form} up={up} />}

        {phase === "feedback" && (
          <div className="mt-2">
            <FeedbackBox
              stepName={`step-${step}`}
              onSubmit={submitFeedback}
              onSkip={skipFeedback}
            />
          </div>
        )}
      </div>

      {/* Bottom navigation */}
      {phase === "form" && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[rgba(9,9,11,0.92)] backdrop-blur-md border-t border-[var(--border)] px-4 sm:px-5 py-3 sm:py-4">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <button
              onClick={prev}
              className="hp-btn hp-btn-tertiary"
              data-testid="ob-prev"
            >
              <ChevronLeft className="w-4 h-4" /> Retour
            </button>
            <button
              onClick={next}
              className="hp-btn hp-btn-primary px-7 py-3 h-12"
              data-testid={step === 4 ? "ob-review" : "ob-next"}
            >
              {step === 4 ? "Voir mon analyse" : "Continuer"}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
